import { ColDef, RowClassParams, ValueFormatterParams } from 'ag-grid-community';
import { t } from 'i18next';

import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { AgGridFilterOption } from 'core/enums';
import { formatDate, formatQuantity } from 'core/utils';
import { TradeAgreementUsage } from 'quotes/enums';
import { ITradeAgreement } from 'quotes/types';
import { formatAgreementCost, isTradeAgreementExpired } from 'quotes/utils';

import {
  InUseStatusCell,
  RadioSelectionCell,
  SpaCostCell,
  SpaCostRegistrationNumCell,
  SpaCostSourceCell,
} from './cells';
import {
  getTradeAgreementUsage,
  isButtonMouseEvent,
  ITradeAgreementUsageContext,
  tradeAgreementUsageLabelKeys,
} from '../../utils';

const textOrPlaceholder = ({ value }: ValueFormatterParams) =>
  value ? String(value) : DEFAULT_PLACEHOLDER;

const dateColumn = (field: keyof ITradeAgreement, headerName: string): ColDef => ({
  field,
  headerName,
  minWidth: 130,
  filter: 'agDateColumnFilter',
  // The raw DB string: the client table's dateFilterComparator parses it with dayjs.
  filterParams: {
    defaultOption: AgGridFilterOption.GREATER_THAN,
    includeBlanksInGreaterThan: true,
  },
  valueFormatter: ({ value }: ValueFormatterParams) =>
    value ? formatDate(value as string) : DEFAULT_PLACEHOLDER,
});

// A real 0 is shown; only a missing value is the dash (formatQuantity alone turns null into 0).
const quantityColumn = (field: keyof ITradeAgreement, headerName: string): ColDef => ({
  field,
  headerName,
  type: 'rightAligned',
  minWidth: 110,
  filter: 'agNumberColumnFilter',
  valueFormatter: ({ value }: ValueFormatterParams) =>
    null == value || '' === value ? DEFAULT_PLACEHOLDER : String(formatQuantity(value)),
});

// heldRowId is null when the line holds no pick or the list no longer offers it.
interface IColumnDefOptions extends Partial<ITradeAgreementUsageContext> {
  onUse?: (agreement: ITradeAgreement) => void;
}

// Functions only: AG-Grid's RowClassRules also takes expression strings, which cannot be called.
type TTradeAgreementRowClassRules = Record<
  'disabledRow' | 'inUseAgreementRow',
  (params: RowClassParams<ITradeAgreement>) => boolean
>;

// rowClassRules, not getRowClass: AG-Grid never removes a class getRowClass stopped returning.
export const getRowClassRules = (
  usageContext: Partial<ITradeAgreementUsageContext> = {}
): TTradeAgreementRowClassRules => ({
  inUseAgreementRow: ({ data }) =>
    [TradeAgreementUsage.IN_USE, TradeAgreementUsage.DEFAULT_IN_USE].includes(
      getTradeAgreementUsage(data, usageContext)
    ),
  disabledRow: ({ data }) => isTradeAgreementExpired(data),
});

export const getColumnDef = (
  costDecimals?: number,
  { defaultRowId = null, heldRowId = null, isDefaultInUse = false, onUse }: IColumnDefOptions = {}
): ColDef[] => {
  const usageContext: ITradeAgreementUsageContext = { defaultRowId, heldRowId, isDefaultInUse };

  return [
    // Sized and pinned like the grid provider's own selection column, which this one replaces.
    {
      colId: 'selection',
      headerName: '',
      cellRenderer: RadioSelectionCell,
      minWidth: 40,
      width: 40,
      maxWidth: 40,
      pinned: 'left',
      lockPinned: true,
      lockPosition: true,
      suppressMovable: true,
      suppressColumnsToolPanel: true,
      suppressHeaderMenuButton: true,
      sortable: false,
      filter: false,
      resizable: false,
      headerClass: 'paddingSmall center',
      cellClass: 'paddingSmall center noDecorationCell',
      cellRendererParams: usageContext,
    },
    // Locked left, so a saved layout that sorts it last still shows it by the radio. A new colId:
    // a saved `hide` of the old one would beat the column definition.
    {
      colId: 'inUseStatus',
      headerName: '',
      // Fits "Default, in use": a ~112px chip plus 32px of padding, with room for wider fonts.
      minWidth: 160,
      maxWidth: 160,
      lockPosition: true,
      lockPinned: true,
      lockVisible: true,
      suppressMovable: true,
      suppressColumnsToolPanel: true,
      sortable: false,
      filter: 'agSetColumnFilter',
      valueGetter: ({ data }: { data: ITradeAgreement }) =>
        data && t(tradeAgreementUsageLabelKeys[getTradeAgreementUsage(data, usageContext)]),
      cellRenderer: InUseStatusCell,
      cellRendererParams: usageContext,
    },
    // The agreement name still travels with the pick but is not listed.
    {
      field: 'spaCostSourceId',
      headerName: t('labelSpaCostSourceId'),
      // Fits an 8-digit version id; the base column width would waste the drawer's room.
      minWidth: 100,
      width: 110,
      maxWidth: 130,
      filter: 'agTextColumnFilter',
      valueFormatter: textOrPlaceholder,
    },
    {
      field: 'spaCostSource',
      headerName: t('labelSpaCostSource'),
      minWidth: 240,
      flex: 1,
      filter: 'agTextColumnFilter',
      cellRenderer: SpaCostSourceCell,
      cellRendererParams: { suppressMouseEventHandling: isButtonMouseEvent },
      // The text filter finds a row by its code or by the source name shown beneath it.
      filterValueGetter: ({ data }: { data: ITradeAgreement }) =>
        data && [data.spaCostSource, data.spaCostSourceName].filter(Boolean).join(' '),
    },
    {
      field: 'spaCostRegistrationNum',
      headerName: t('labelSpaCostRegistrationNum'),
      minWidth: 140,
      filter: 'agTextColumnFilter',
      valueFormatter: textOrPlaceholder,
      cellRenderer: SpaCostRegistrationNumCell,
      cellRendererParams: { suppressMouseEventHandling: isButtonMouseEvent },
    },
    {
      field: 'spaCost',
      headerName: t('labelSpaCost'),
      type: 'rightAligned',
      // Fits "Use default SPA" with its icon when resized to the minimum.
      minWidth: 160,
      filter: 'agNumberColumnFilter',
      valueFormatter: ({ data }: ValueFormatterParams<ITradeAgreement>) =>
        formatAgreementCost(data, costDecimals),
      cellRenderer: SpaCostCell,
      cellRendererParams: { ...usageContext, onUse },
    },
    dateColumn('spaCostStartDate', t('labelSpaCostStartDate')),
    dateColumn('spaCostEndDate', t('labelSpaCostEndDate')),
    quantityColumn('spaQuantityRemaining', t('labelSpaQuantityRemaining')),
    quantityColumn('spaQuantityUsage', t('labelSpaQuantityUsage')),
  ];
};
